// @ts-check

/** @file Browser/native proof bridge for real per-project SQLite replicas. */

import Configuration from "velocious/build/src/configuration.js"

import Option from "../models/option"
import ProjectReplicaRegistryEntry from "../models/project-replica-registry-entry"
import ReplicaMetadata from "../models/replica-metadata"
import Sync from "../models/sync"
import Task from "../models/task"
import runLocalReplicaMutation from "../database/local-replica-mutation"
import {PROJECT_REPLICA_DATABASE_IDENTIFIER, closeProjectReplica, openProjectReplica, resetProjectReplicasForTests} from "../database/project-replica"
import {waitForInitialCurrentUserLoad} from "../auth/current-user-startup"
import {clearSessionToken, setSessionToken} from "../shared/session-option"
import {flushLocalDatabaseWrites} from "../shared/local-database-persistence"
import {projectSyncConnectivity} from "../sync/project-sync-connectivity"
import {projectSyncLifecycle} from "../sync/project-sync-lifecycle"

/** @import {RequireMigrationContextType} from "velocious/build/src/database/migrator/types.js" */
/** @import TenantHandle from "velocious/build/src/tenants/tenant-handle.js" */
/** @typedef {{markerKey: string, markerValue: string, projectId: string, taskId: string, taskName: string, userId: string}} CreateQueuedTaskArguments */
/** @typedef {{markerKey: string, projectId: string, taskId: string, userId: string}} SnapshotArguments */
/** @typedef {{firstProjectId: string, secondProjectId: string, sharedTaskId: string, userId: string}} SwitchProjectsArguments */
/** @typedef {{projectIds: string[], userId: string}} ChurnArguments */

/** System-test-only public operations over actual configured SQLite drivers. */
export default class LocalReplicaDatabaseProbe {
  /**
   * Creates one queued task and closes its retained project database.
   * @param {CreateQueuedTaskArguments} args Queued task and replica identity.
   * @returns {Promise<{coordinatorState: string, syncId: string, taskId: string}>} Durable mutation and nonblocking lifecycle state.
   */
  static async createQueuedTask({markerKey, markerValue, projectId, taskId, taskName, userId}) {
    projectSyncConnectivity.requestFailed()
    // The fixture session tokens are real backend sessions, so the app's own
    // sync lifecycle can authenticate and open the same project replica the
    // probe is about to own (seen on Android: concurrent opens collide and
    // the background cycle reports sync-failed). The probe owns the app
    // databases during its work: stop the app-owned lifecycle first, exactly
    // like sign-out does.
    await projectSyncLifecycle.stop()
    await setSessionToken({
      expiresAt: new Date(Date.now() + 60 * 60 * 1000),
      token: "project-replica-system-test-session",
      userId
    })
    await Configuration.current().withTransaction({
      databaseIdentifier: "default",
      name: "Persist project replica system-test catalog marker"
    }, async (operation) => {
      await operation.forModel(Option).create({key: markerKey, value: markerValue})
    })
    await flushLocalDatabaseWrites()
    const replica = await openProjectReplica({migrations: projectMigrations(), projectId, routingEpoch: 1, userId})
    await replica.coordinator.waitForCurrentRun()
    const coordinatorState = replica.coordinator.status().state
    const mutation = await runLocalReplicaMutation(replica.handle, async ({forModel, queueMutation}) => {
      const task = await forModel(Task).create({id: taskId, name: taskName, projectId, state: "open"})
      const sync = await queueMutation({data: {id: task.id(), name: task.name(), projectId}, resource: task, syncType: "update"})
      return {syncId: sync.id(), taskId: task.id()}
    })
    await closeProjectReplica(replica)
    return {...mutation.result, coordinatorState}
  }

  /**
   * Reads one project after restart through a newly captured tenant handle.
   * @param {SnapshotArguments} args Snapshot identity.
   * @returns {Promise<Record<string, string | number | null>>} Persisted catalog and project state.
   */
  static async snapshot({markerKey, projectId, taskId, userId}) {
    projectSyncConnectivity.requestFailed()
    // Native startup deliberately exposes its system-test anchor before auth
    // initialization so the bridge can connect. Wait for that initialization
    // to finish before replacing the persisted fixture token; otherwise
    // startup's clearSessionToken can delete an Option between save and reload.
    await waitForInitialCurrentUserLoad()
    // The persisted fixture session is a real backend session, so after a
    // restart the app's own sync lifecycle authenticates and starts a catalog
    // cycle that holds the default-database checkout the probe needs (10s
    // timeout). The probe owns the app databases during its work: stop the
    // app-owned lifecycle first, exactly like sign-out does.
    await projectSyncLifecycle.stop()
    await setSessionToken({
      expiresAt: new Date(Date.now() + 60 * 60 * 1000),
      token: "project-replica-system-test-restart-session",
      userId
    })
    const replica = await openProjectReplica({migrations: projectMigrations(), projectId, routingEpoch: 1, userId})
    const snapshot = await replica.handle.databaseOperation({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
      const task = await operation.forModel(Task).findBy({id: taskId})
      const metadata = await operation.forModel(ReplicaMetadata).findBy({key: "mutation-lamport-sequence"})
      return {
        metadataValue: metadata?.value() ?? null,
        queuedMutationCount: await operation.forModel(Sync).where({resourceId: taskId}).count(),
        taskName: task?.name() ?? null
      }
    })
    await closeProjectReplica(replica)
    const marker = await Option.findBy({key: markerKey})
    const registry = await ProjectReplicaRegistryEntry.findBy({projectId, userId})
    return {...snapshot, markerValue: marker?.value() ?? null, registryState: registry?.state() ?? null}
  }

  /**
   * Proves repeated concurrent switching never aliases equal row IDs.
   * @param {SwitchProjectsArguments} args Switching identities.
   * @returns {Promise<{values: (string | null)[]}>} Repeated isolated task values.
   */
  static async switchProjects({firstProjectId, secondProjectId, sharedTaskId, userId}) {
    const first = await openProjectReplica({migrations: projectMigrations(), projectId: firstProjectId, routingEpoch: 2, userId})
    const second = await openProjectReplica({migrations: projectMigrations(), projectId: secondProjectId, routingEpoch: 3, userId})
    await Promise.all([
      writeTask(first.handle, {id: sharedTaskId, name: "first-project", projectId: firstProjectId}),
      writeTask(second.handle, {id: sharedTaskId, name: "second-project", projectId: secondProjectId})
    ])
    const values = await Promise.all([
      taskName(first.handle, sharedTaskId), taskName(second.handle, sharedTaskId), taskName(first.handle, sharedTaskId)
    ])
    await closeProjectReplica(first)
    await closeProjectReplica(second)
    return {values}
  }

  /**
   * Proves bounded handle churn and pinned-handle refusal.
   * @param {ChurnArguments} args Three project identities for a two-handle bound.
   * @returns {Promise<{maxOpenHandles: number, openCount: number, refusal: string | null}>} Lifecycle diagnostics.
   */
  static async churn({projectIds, userId}) {
    const [first, second] = await Promise.all(projectIds.slice(0, 2).map(async (projectId) => await openProjectReplica({
      migrations: projectMigrations(), projectId, routingEpoch: 1, userId
    })))
    await Promise.all([
      writeTask(first.handle, {id: "11111111-aaaa-4aaa-8aaa-111111111111", name: "dirty-first", projectId: projectIds[0]}),
      writeTask(second.handle, {id: "22222222-bbbb-4bbb-8bbb-222222222222", name: "dirty-second", projectId: projectIds[1]})
    ])
    let refusal = null
    await first.handle.withPin({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async () => {
      await second.handle.withPin({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async () => {
        try {
          await openProjectReplica({migrations: projectMigrations(), projectId: projectIds[2], routingEpoch: 1, userId})
        } catch (error) {
          refusal = error instanceof Error ? error.message : String(error)
        }
      })
    })
    await first.handle.flush({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})
    await second.handle.flush({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})
    const third = await openProjectReplica({migrations: projectMigrations(), projectId: projectIds[2], routingEpoch: 1, userId})
    const diagnostics = Configuration.current().inspectFrontendTenantSqliteHandles()
    await closeProjectReplica(second)
    await closeProjectReplica(third)
    await closeProjectReplica(first)
    return {maxOpenHandles: diagnostics.maxOpenHandles, openCount: diagnostics.openCount, refusal}
  }

  /**
   * Deletes only exact system-test project databases registered by the probe.
   * @param {string | undefined} markerKey Optional catalog marker to remove.
   * @returns {Promise<void>} Completion after exact cleanup.
   */
  static async cleanup(markerKey) {
    await resetProjectReplicasForTests({migrations: projectMigrations()})
    if (markerKey) await (await Option.findBy({key: markerKey}))?.destroy()
    await clearSessionToken()
    projectSyncConnectivity.retry()
  }
}

/**
 * Loads bundled project migrations from Metro.
 * @returns {RequireMigrationContextType} Bundled per-project migrations.
 */
function projectMigrations() {
  // @ts-expect-error Metro's require.context extension is not in Node's require type.
  return require.context("../database/project-replica-migrations", true, /\.\/(\d{14})-(.+)\.js$/)
}

/**
 * Writes one task through an immutable handle.
 * @param {TenantHandle} handle Captured project database boundary.
 * @param {{id: string, name: string, projectId: string}} attributes Fields persisted in the selected replica.
 * @returns {Promise<void>} Completion after the write.
 */
async function writeTask(handle, attributes) {
  await handle.transaction({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    await operation.forModel(Task).create({...attributes, state: "open"})
  })
}

/**
 * Reads a task name from one immutable handle.
 * @param {TenantHandle} handle Immutable project handle.
 * @param {string} taskId Task UUID.
 * @returns {Promise<string | null>} Stored task name.
 */
async function taskName(handle, taskId) {
  return await handle.databaseOperation({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    return (await operation.forModel(Task).findBy({id: taskId}))?.name() ?? null
  })
}
